import * as endpoints from "../../lib/api/endpoints";
import cn from "../../lib/classnames";
import debounce from "../../scripts/debounce";
import KeyCodes from "../../constants/keyCodes";
import LinedInput, {InputType} from "../LinedInput";
import React, {useState, useRef} from "react";
import Spinner from "../Spinner";
import styles from "./CollegeInput.module.css";

const DEBOUNCE_MS = 300;
const DEBOUNCE_OPTS = {maxWait: 300};

type Props = React.ComponentProps<"input"> & {
    id: string;
    onCollegeSelected: (s: string) => void;
    label: string;
    validationMessage?: string[];
    initialSearch?: string;
};

const strings = {
    noSuggestions: "No Suggestions",
};

function CollegeInput(
    {
        className,
        id,
        label,
        name,
        onBlur,
        onCollegeSelected,
        required,
        validationMessage,
        initialSearch = "",
    }: Props,
    ref: React.ForwardedRef<HTMLInputElement>,
) {
    const [search, setSearch] = useState(initialSearch);
    const [results, setResults] = useState<string[]>([]);
    const [loading, setLoading] = useState(false);
    const [isResultVisible, setIsResultVisible] = useState(false);
    const [keyboardFocusIndex, setKeyboardFocusIndex] = useState<number>(0);
    const [hasFetchedResults, setHasFetchedResults] = useState(false);

    function searchColleges(newSearch: string) {
        if (!newSearch) {
            setResults([]);
            return;
        }

        setLoading(true);
        setHasFetchedResults(true);

        endpoints
            .collegeSearch(newSearch)
            .then((response) => setResults(response))
            .finally(() => setLoading(false));
    }

    function moveFocusIndex(up: boolean) {
        if (!results.length) {
            setKeyboardFocusIndex(0);
        } else if (up) {
            setKeyboardFocusIndex((curr) => Math.max(0, curr - 1));
        } else {
            setKeyboardFocusIndex((curr) => Math.min(results.length - 1, curr + 1));
        }

        if (keyboardFocusIndex < results.length) {
            const element = document.getElementById(`search-result-${keyboardFocusIndex}`);
            element?.scrollIntoView({behavior: "smooth", block: "nearest", inline: "start"});
        }
    }

    const debouncedSearch = useRef(debounce(searchColleges, DEBOUNCE_MS, DEBOUNCE_OPTS)).current;

    function renderMenu() {
        let content;
        if (results.length) {
            content = results.map((result, i) => (
                // NOTE(CK): Using mousedown instead of click so that events fire before blur
                <button
                    key={i}
                    id={`search-result-${i}`}
                    type="button"
                    className={cn(
                        styles.result,
                        i === keyboardFocusIndex ? styles.keyboardFocused : undefined,
                    )}
                    onMouseDown={() => {
                        setSearch(result);
                        onCollegeSelected(result);
                    }}
                >
                    {result}
                </button>
            ));
        } else if (loading || !hasFetchedResults) {
            content = (
                <div className={styles.menuMessage}>
                    <Spinner className={styles.spinner} isVisible={true} />
                </div>
            );
        } else {
            content = <div className={styles.menuMessage}>{strings.noSuggestions}</div>;
        }
        return <div className={styles.resultsContainer}>{content}</div>;
    }

    return (
        <div className={cn(styles.container, className)}>
            <LinedInput
                ref={ref}
                autoComplete="off"
                id={id}
                value={search}
                onChange={(e) => {
                    const value = e.target.value;
                    setSearch(value);
                    debouncedSearch(value);
                    if (!value.length) {
                        setHasFetchedResults(false);
                    }
                }}
                label={label}
                loading={loading}
                placeholder={label}
                onFocus={() => setIsResultVisible(true)}
                onBlur={(e) => {
                    onCollegeSelected(search);
                    if (onBlur) {
                        onBlur(e);
                    }
                    setIsResultVisible(false);
                }}
                onKeyDown={(e) => {
                    // TODO: .keyCode is deprecated, replace with .key
                    if (e.keyCode !== KeyCodes.Tab) {
                        setIsResultVisible(true);
                    }
                    if (e.keyCode === KeyCodes.Up || e.keyCode === KeyCodes.Down) {
                        e.preventDefault();
                        if (isResultVisible) {
                            moveFocusIndex(e.keyCode === KeyCodes.Up);
                        } else {
                            setIsResultVisible(true);
                        }
                    }
                    if (e.keyCode === KeyCodes.Enter && keyboardFocusIndex < results.length) {
                        e.preventDefault();
                        setSearch(results[keyboardFocusIndex]);
                        onCollegeSelected(results[keyboardFocusIndex]);
                        setIsResultVisible(false);
                    }
                }}
                required={required}
                type={InputType.Text}
                validationMessage={validationMessage}
                name={name}
            />
            {isResultVisible && search.length ? renderMenu() : null}
        </div>
    );
}

export default React.forwardRef<HTMLInputElement, Props>(CollegeInput);
